import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import ProfileForm from "@/components/ProfileForm";
import Spinner from "@/components/Spinner";
import { MissionEyebrow, usd } from "@/components/Mission";
import { getProfile, setProfile, profileForApi } from "@/lib/profileStore";

const DIFFICULTY_PILL = {
  easy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  hard: "bg-red-50 text-red-700 border-red-200",
};

const CATEGORY_TITLE = {
  before_apply: "Before you apply",
  when_applying: "When you apply",
  after_acceptance: "After acceptance",
  four_year: "Four-year savings",
};

export default function MoneySaverPage() {
  const navigate = useNavigate();
  const [profile, setProfileLocal] = useState(getProfile());
  const [playbook, setPlaybook] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const completed = new Set(profile.saver_completed || []);

  const generate = async () => {
    setError("");
    if (!profile.income_bracket) return setError("Pick an income range — biggest aid wins depend on it.");
    setBusy(true);
    try {
      const r = await api.post("/aw/saver/playbook", { profile: profileForApi(profile) });
      setPlaybook(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't build your playbook. Try again.");
    } finally { setBusy(false); }
  };

  const toggle = (id) => {
    const next = new Set(completed);
    if (next.has(id)) next.delete(id); else next.add(id);
    const arr = Array.from(next);
    setProfileLocal((p) => ({ ...p, saver_completed: arr }));
    setProfile({ saver_completed: arr });
  };

  const onAction = (item) => {
    const k = item.action_kind;
    const ap = item.action_payload || {};
    if (k === "open_dream_school") navigate(`/app/dream-school?school=${encodeURIComponent(ap.school || (item.schools?.[0] || ""))}`);
    else if (k === "draft_appeal") navigate("/appeal");
    else if (k === "view_school") navigate("/netprice");
    else if (k === "add_to_calendar") navigate("/app/calendar");
    else if (k === "generate_waiver") navigate("/chat");
  };

  const items = playbook?.items || [];
  const total = playbook?.total_potential_usd || 0;
  const unlocked = items
    .filter((i) => completed.has(i.id))
    .reduce((s, i) => s + (i.est_savings_usd || 0), 0);
  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it); return acc;
  }, {});

  return (
    <AppShell>
      <div className="py-10" data-testid="saver-root">
        <MissionEyebrow testId="saver-mission" />
        <h1 className="font-heading italic text-4xl sm:text-5xl text-[#0f172a] mb-3">Money Saver Guide</h1>
        <p className="text-slate-600 mb-8 max-w-2xl">
          Every action ranked by dollar impact — specific to your income, GPA, state, and college list.
        </p>

        <ProfileForm profile={profile} onChange={setProfileLocal} requireFields={["income"]} compact={!!playbook}/>

        {!playbook && (
          <div className="flex items-center justify-between gap-3 mb-8">
            <button onClick={generate} disabled={busy} data-testid="saver-generate-btn"
              className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-6 py-3 disabled:opacity-60">
              {busy ? "Building your playbook…" : "Build my playbook →"}
            </button>
            {error && <span className="text-sm text-red-700" data-testid="saver-error">{error}</span>}
          </div>
        )}

        {!playbook && busy && (
          <Spinner variant="block" label="Reasoning over your profile, your colleges, and the federal aid policy data." testId="saver-spinner" />
        )}

        {playbook && (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mb-10">
              <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6" data-testid="saver-total-card">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Potential savings</div>
                <div className="font-heading italic text-4xl text-[#0f172a]">{usd(total)}</div>
                <div className="text-xs text-slate-500 mt-1">across {items.length} actions</div>
              </div>
              <div className="aw-card bg-[#eff6ff] border border-[#bfdbfe] rounded-2xl p-6" data-testid="saver-unlocked-card">
                <div className="text-xs uppercase tracking-widest text-[#1d4ed8] mb-2">Unlocked so far</div>
                <div className="font-heading italic text-4xl text-[#1d4ed8]">{usd(unlocked)}</div>
                <div className="text-xs text-[#1d4ed8]/80 mt-1">{completed.size} actions complete</div>
              </div>
              <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 flex flex-col">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Share your wins</div>
                <p className="text-sm text-slate-600 mb-3">A snapshot — works on Instagram & socials.</p>
                <button onClick={() => navigate("/app/saver/share", { state: { unlocked, total } })}
                  data-testid="saver-share-link"
                  className="text-sm text-[#1d4ed8] hover:underline mt-auto self-start">Open share card →</button>
              </div>
            </div>

            {playbook.summary && (
              <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 mb-8" data-testid="saver-summary">
                <div className="text-xs uppercase tracking-widest text-[#1d4ed8] mb-2">The biggest opportunity</div>
                <p className="text-[#0f172a] leading-relaxed">{playbook.summary}</p>
              </div>
            )}

            {Object.entries(CATEGORY_TITLE).map(([cat, label]) => {
              const list = grouped[cat] || [];
              if (list.length === 0) return null;
              return (
                <section key={cat} className="mb-10" data-testid={`saver-cat-${cat}`}>
                  <h2 className="font-heading italic text-2xl text-[#0f172a] mb-4">{label}</h2>
                  <div className="space-y-3">
                    {list.map((it) => {
                      const done = completed.has(it.id);
                      return (
                        <div key={it.id}
                          className={`aw-card rounded-2xl border p-5 transition ${done ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-[#bfdbfe]"}`}
                          data-testid={`saver-action-${it.id}`}>
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                {it.time_sensitive && (
                                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                                    Time-sensitive{it.deadline ? ` · ${it.deadline}` : ""}
                                  </span>
                                )}
                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${DIFFICULTY_PILL[it.difficulty] || ""}`}>{it.difficulty}</span>
                                {Array.isArray(it.schools) && it.schools.length > 0 && (
                                  <span className="text-[10px] text-slate-500">{it.schools.slice(0, 3).join(" · ")}</span>
                                )}
                              </div>
                              <h3 className="text-base font-medium text-[#0f172a]">{it.title}</h3>
                              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{it.description}</p>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-heading italic text-3xl text-[#1d4ed8]">{usd(it.est_savings_usd)}</div>
                              <div className="text-xs text-slate-500">est. savings</div>
                            </div>
                          </div>
                          <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-[#e5e7eb]">
                            {it.action_kind && it.action_kind !== "none" && (
                              <button onClick={() => onAction(it)} data-testid={`saver-action-${it.id}-do`}
                                className="text-xs text-[#1d4ed8] hover:underline">
                                {labelForActionKind(it.action_kind)} →
                              </button>
                            )}
                            <button onClick={() => toggle(it.id)} data-testid={`saver-action-${it.id}-toggle`}
                              className={`text-xs rounded-full px-4 py-1.5 border transition ${done ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700" : "bg-white border-[#bfdbfe] text-[#0f172a] hover:border-[#1d4ed8]"}`}>
                              {done ? "✓ Done" : "Mark done"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}

            <div className="flex items-center justify-end gap-3 pt-6 border-t border-[#e5e7eb]">
              <button onClick={generate} disabled={busy} data-testid="saver-refresh-btn"
                className="text-sm text-[#1d4ed8] hover:underline disabled:opacity-50">
                {busy ? "Refreshing…" : "Re-run with updated profile"}
              </button>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

function labelForActionKind(k) {
  if (k === "draft_appeal") return "Draft appeal letter";
  if (k === "generate_waiver") return "Generate waiver";
  if (k === "add_to_calendar") return "Add to calendar";
  if (k === "open_dream_school") return "Run Dream School Rescue";
  if (k === "view_school") return "Open Net Price";
  return "Take action";
}
